// frontend/react-app/src/App.js
import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import RequireAuth from "./components/RequireAuth";
import ToastHost from "./components/ToastHost";
import FrontendErrorMonitor, {
  FrontendErrorBoundary,
} from "./components/FrontendErrorMonitor";
import {
  bootstrapPlatformBrand,
  getStudentContentAccessState,
  getStudentLandingPath,
} from "./api";
import { getStoredUser, hasClientSession } from "./api/session";
import { initFrontendMonitoring } from "./lib/monitoring";

initFrontendMonitoring();

const HomePage = lazy(() => import("./pages/HomePage"));
const AdminMfaChallenge = lazy(() => import("./pages/AdminMfaChallenge"));
const AdminMfaSetupRequired = lazy(() => import("./pages/AdminMfaSetupRequired"));
const Signup = lazy(() => import("./pages/Signup"));
const Course = lazy(() => import("./pages/Course"));
const NoAccess = lazy(() => import("./pages/NoAccess"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SupportPage = lazy(() => import("./pages/SupportPage"));

const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const StudentProfile = lazy(() => import("./pages/StudentProfile"));
const TaskPage = lazy(() => import("./pages/TaskPage"));
const StudentLayout = lazy(() => import("./layout/StudentLayout"));
const StudentHome = lazy(() => import("./pages/StudentHome"));
const ResumeLearning = lazy(() => import("./pages/ResumeLearning"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const Train = lazy(() => import("./pages/Train"));
const TrainTask = lazy(() => import("./pages/TrainTask"));

const PlatformAdmin = lazy(() => import("./Admin/Pages/PlatformAdmin"));
const AdminDashboard = lazy(() => import("./Admin/Pages/AdminDashboard"));
const PlatformSettings = lazy(() => import("./Admin/Pages/PlatformSettings"));
const AdminCourses = lazy(() => import("./Admin/Pages/AdminCourses"));
const AdminCoursePaths = lazy(() => import("./Admin/Pages/AdminCoursePaths"));
const AdminPathDays = lazy(() => import("./Admin/Pages/AdminPathDays"));
const AdminDayTasks = lazy(() => import("./Admin/Pages/AdminDayTasks"));
const AdminTaskEditor = lazy(() => import("./Admin/Pages/AdminTaskEditor"));
const AdminStudents = lazy(() => import("./Admin/Pages/AdminStudents"));
const AdminSubscriptions = lazy(() => import("./Admin/Pages/AdminSubscriptions"));
const AdminOperations = lazy(() => import("./Admin/Pages/AdminOperations"));
const AdminSupportInbox = lazy(() => import("./Admin/Pages/AdminSupportInbox"));
const AdminSecurity = lazy(() => import("./Admin/Pages/AdminSecurity"));
const AdminStudentProfile = lazy(() => import("./Admin/Pages/AdminStudentProfile"));
const AdminAssessmentTags = lazy(() => import("./Admin/Pages/AdminAssessmentTags"));
const AdminAssessmentBank = lazy(() =>
  import("./Admin/Pages/AdminAssessmentBank/AdminAssessmentBankBanksPage")
);
const AdminAssessmentBankChapters = lazy(() =>
  import("./Admin/Pages/AdminAssessmentBank/AdminAssessmentBankPathsPage")
);
const AdminAssessmentBankSections = lazy(() =>
  import("./Admin/Pages/AdminAssessmentBank/AdminAssessmentBankDaysPage")
);
const AdminAssessmentBankBranches = lazy(() =>
  import("./Admin/Pages/AdminAssessmentBank/AdminAssessmentBankTasksPage")
);
const AdminAssessmentBankQuestions = lazy(() =>
  import("./Admin/Pages/AdminAssessmentBank/AdminAssessmentBankQuestions")
);
const AdminContentBank = lazy(() =>
  import("./Admin/Pages/AdminContentBank/AdminContentBankBanksPage")
);
const AdminContentBankPaths = lazy(() =>
  import("./Admin/Pages/AdminContentBank/AdminContentBankPathsPage")
);
const AdminContentBankSections = lazy(() =>
  import("./Admin/Pages/AdminContentBank/AdminContentBankSectionsPage")
);
const AdminContentBankTasks = lazy(() =>
  import("./Admin/Pages/AdminContentBank/AdminContentBankTasksPage")
);
const AdminContentBankItems = lazy(() =>
  import("./Admin/Pages/AdminContentBank/AdminContentBankItems")
);
const AdminAccounts = lazy(() => import("./Admin/Pages/AdminAccounts"));

function isPlatformAdmin(user) {
  return user?.role === "admin";
}

function isStudent(user) {
  return user?.role === "student";
}

function RouteFallback() {
  return <div style={routeFallback}>نجهّز صفحتك...</div>;
}

function LazyScreen({ children }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function RequirePlatformAdmin({ children }) {
  const user = getStoredUser();

  if (!isPlatformAdmin(user)) {
    return <Navigate to="/forbidden" replace />;
  }

  return children;
}

function RequireStudentContentAccess({ children }) {
  const user = getStoredUser();
  const access = getStudentContentAccessState(user);

  if (!isStudent(user)) {
    return <Navigate to="/forbidden" replace />;
  }

  if (!access.allowed) {
    return <Navigate to="/no-access" replace state={{ reason: access.reason }} />;
  }

  return children;
}

function StudentProtectedRoute({ children }) {
  return (
    <RequireAuth>
      <RequireStudentContentAccess>
        <LazyScreen>
          <StudentLayout>{children}</StudentLayout>
        </LazyScreen>
      </RequireStudentContentAccess>
    </RequireAuth>
  );
}

function StudentAuthOnlyRoute({ children }) {
  return (
    <RequireAuth>
      <LazyScreen>
        <StudentLayout>{children}</StudentLayout>
      </LazyScreen>
    </RequireAuth>
  );
}

function AdminProtectedRoute({ children }) {
  return (
    <RequireAuth>
      <RequirePlatformAdmin>
        <LazyScreen>{children}</LazyScreen>
      </RequirePlatformAdmin>
    </RequireAuth>
  );
}

function HomeRedirect() {
  const hasSession = hasClientSession();
  const user = getStoredUser();

  if (hasSession && isPlatformAdmin(user)) {
    return <Navigate to="/admin" replace />;
  }

  if (hasSession && isStudent(user)) {
    return <Navigate to={getStudentLandingPath(user)} replace />;
  }

  return (
    <LazyScreen>
      <HomePage />
    </LazyScreen>
  );
}

export default function App() {
  const [brandReady, setBrandReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    bootstrapPlatformBrand().finally(() => {
      if (mounted) setBrandReady(true);
    });

    return () => {
      mounted = false;
    };
  }, []);

  if (!brandReady) {
    return <div style={routeFallback}>نجهّز صفحتك...</div>;
  }

  return (
    <BrowserRouter>
      <FrontendErrorBoundary>
        <FrontendErrorMonitor />
        <ToastHost />

        <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route
          path="/admin/mfa-setup"
          element={
            <LazyScreen>
              <AdminMfaSetupRequired />
            </LazyScreen>
          }
        />
        <Route
          path="/admin/mfa-challenge"
          element={
            <LazyScreen>
              <AdminMfaChallenge />
            </LazyScreen>
          }
        />

        <Route
          path="/signup"
          element={
            <LazyScreen>
              <Signup />
            </LazyScreen>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <LazyScreen>
              <ForgotPassword />
            </LazyScreen>
          }
        />
        <Route
          path="/reset-password"
          element={
            <LazyScreen>
              <ResetPassword />
            </LazyScreen>
          }
        />
        <Route
          path="/support"
          element={
            <LazyScreen>
              <SupportPage />
            </LazyScreen>
          }
        />

        <Route
          path="/no-access"
          element={
            <LazyScreen>
              <NoAccess />
            </LazyScreen>
          }
        />
        <Route
          path="/forbidden"
          element={
            <LazyScreen>
              <AccessDenied />
            </LazyScreen>
          }
        />
        <Route
          path="/not-found"
          element={
            <LazyScreen>
              <NotFound />
            </LazyScreen>
          }
        />

        <Route
          path="/student"
          element={
            <StudentProtectedRoute>
              <StudentHome />
            </StudentProtectedRoute>
          }
        />
        <Route path="/student/home" element={<Navigate to="/student" replace />} />

        <Route
          path="/student/resume"
          element={
            <StudentProtectedRoute>
              <ResumeLearning />
            </StudentProtectedRoute>
          }
        />

        <Route
          path="/student/dashboard"
          element={
            <StudentProtectedRoute>
              <StudentDashboard />
            </StudentProtectedRoute>
          }
        />

        <Route
          path="/student/subscription"
          element={
            <StudentAuthOnlyRoute>
              <Subscribe />
            </StudentAuthOnlyRoute>
          }
        />

        <Route
          path="/student/training"
          element={
            <StudentProtectedRoute>
              <Train />
            </StudentProtectedRoute>
          }
        />

        <Route
          path="/student/training/task/:trainingId"
          element={
            <StudentProtectedRoute>
              <TrainTask />
            </StudentProtectedRoute>
          }
        />

        <Route
          path="/student/profile"
          element={
            <StudentProtectedRoute>
              <StudentProfile />
            </StudentProtectedRoute>
          }
        />

        <Route
          path="/student/course"
          element={
            <StudentProtectedRoute>
              <Course />
            </StudentProtectedRoute>
          }
        />

        <Route
          path="/student/course/task/:taskId"
          element={
            <StudentProtectedRoute>
              <TaskPage />
            </StudentProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <PlatformAdmin />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/dashboard"
          element={
            <AdminProtectedRoute>
              <AdminDashboard />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/accounts"
          element={
            <AdminProtectedRoute>
              <AdminAccounts />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/settings"
          element={
            <AdminProtectedRoute>
              <PlatformSettings />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/courses"
          element={
            <AdminProtectedRoute>
              <AdminCourses />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/courses/:courseId/paths"
          element={
            <AdminProtectedRoute>
              <AdminCoursePaths />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/courses/:courseId/paths/:pathId/days"
          element={
            <AdminProtectedRoute>
              <AdminPathDays />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/courses/:courseId/paths/:pathId/days/:dayId/tasks"
          element={
            <AdminProtectedRoute>
              <AdminDayTasks />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/courses/:courseId/paths/:pathId/days/:dayId/tasks/edit/:taskId"
          element={
            <AdminProtectedRoute>
              <AdminTaskEditor />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/students"
          element={
            <AdminProtectedRoute>
              <AdminStudents />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/students/:id"
          element={
            <AdminProtectedRoute>
              <AdminStudentProfile />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/subscriptions"
          element={
            <AdminProtectedRoute>
              <AdminSubscriptions />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/security"
          element={
            <AdminProtectedRoute>
              <AdminSecurity />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/operations"
          element={
            <AdminProtectedRoute>
              <AdminOperations />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/support"
          element={
            <AdminProtectedRoute>
              <AdminSupportInbox />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/assessment-tags"
          element={
            <AdminProtectedRoute>
              <AdminAssessmentTags />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/assessment-bank"
          element={
            <AdminProtectedRoute>
              <AdminAssessmentBank />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/assessment-bank/:bankId/chapters"
          element={
            <AdminProtectedRoute>
              <AdminAssessmentBankChapters />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/assessment-bank/:bankId/chapters/:chapterId/sections"
          element={
            <AdminProtectedRoute>
              <AdminAssessmentBankSections />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/assessment-bank/:bankId/chapters/:chapterId/sections/:sectionId/branches"
          element={
            <AdminProtectedRoute>
              <AdminAssessmentBankBranches />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/assessment-bank/:bankId/chapters/:chapterId/sections/:sectionId/branches/:branchId/questions"
          element={
            <AdminProtectedRoute>
              <AdminAssessmentBankQuestions />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/content-bank"
          element={
            <AdminProtectedRoute>
              <AdminContentBank />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/content-bank/:bankId/paths"
          element={
            <AdminProtectedRoute>
              <AdminContentBankPaths />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/content-bank/:bankId/paths/:bankPathId/sections"
          element={
            <AdminProtectedRoute>
              <AdminContentBankSections />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/content-bank/:bankId/paths/:bankPathId/sections/:sectionId/tasks"
          element={
            <AdminProtectedRoute>
              <AdminContentBankTasks />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/content-bank/:bankId/paths/:bankPathId/sections/:sectionId/tasks/:bankTaskId/items"
          element={
            <AdminProtectedRoute>
              <AdminContentBankItems />
            </AdminProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/not-found" replace />} />
        </Routes>
      </FrontendErrorBoundary>
    </BrowserRouter>
  );
}

const routeFallback = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f7f9f8",
  color: "#667085",
  fontSize: 14,
  fontWeight: 800,
  padding: 24,
};
